const SAMPLE_RATE = 0.1;
const VIEWABLE_MARK_NAME = 'googleAdDivViewable';
const IN_VIEWPORT_AT_LOAD_MARK_NAME = 'googleAdInViewportAtLoad';

const isElementPartiallyInViewport = (el) => {
  const rect = el.getBoundingClientRect();
  const vh = window.innerHeight;
  const vw = window.innerWidth;
  return rect.bottom > 0 && rect.right > 0 && rect.top < vh && rect.left < vw;
};

const hasGoogleAdInViewportAtLoadMark = (divId) =>
  performance.getEntriesByType('mark').some(
    (m) => m.name === IN_VIEWPORT_AT_LOAD_MARK_NAME && m.detail?.divId === divId
  );

const markGoogleAdInViewportAtLoadIfNeeded = (el) => {
  if (!el?.id || hasGoogleAdInViewportAtLoadMark(el.id)) return;
  if (!isElementPartiallyInViewport(el)) return;
  performance.mark(IN_VIEWPORT_AT_LOAD_MARK_NAME, { detail: { divId: el.id } });
};

const pushMeasureToDataLayer = (measureName, adType, position) => {
  const entries = performance.getEntriesByName(measureName, 'measure');
  const entry = entries[entries.length - 1];
  if (!entry) return;

  if (Math.random() >= SAMPLE_RATE) return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'trackable event',
    eventName: 'Ad Performance',
    eventParams: {
      ad_performance_measure_name: measureName,
      ad_performance_duration: Math.round(entry.duration),
      ad_performance_start: Math.round(entry.startTime),
      ad_type: adType,
      position,
    },
  });
};

const measureEventToEvent = (slotName, divId, fromEvent, toEvent, adType) => {
  const marks = performance.getEntriesByType('mark');

  let fromMark;
  let toMark;

  if (fromEvent === 'pageLoadStart') {
    fromMark = marks.find(m => m.name === 'pageLoadStart');
  } else {
    fromMark = marks.find(m => m.name === fromEvent && m.detail.position === slotName);
  }

  if (toEvent === VIEWABLE_MARK_NAME) {
    toMark = marks.find(m => m.name === toEvent && m.detail.divId === divId);
  } else {
    toMark = marks.find(m => m.name === toEvent && m.detail.position === slotName);
  }

  if (!fromMark || !toMark) return;

  performance.measure(`${fromEvent} to ${toEvent}`, {
    detail: { 'position': slotName, 'divId': divId },
    start: fromMark.startTime,
    end: toMark.startTime,
  });

  pushMeasureToDataLayer(`${fromEvent} to ${toEvent}`, adType, slotName);
};

const measureViewableToEvent = (elementId, position, endEventName, adType) => {
  const marks = performance.getEntriesByType('mark');
  const viewableMark = marks.find(m => m.name === VIEWABLE_MARK_NAME && m.detail.divId === elementId);
  const endMark = marks.find(m => m.name === endEventName && m.detail.divId === elementId);

  if (!viewableMark || !endMark) return;
    
  performance.measure(`${VIEWABLE_MARK_NAME} to ${endEventName}`, {
    detail: { 'position': position, 'divId': elementId },
    start: viewableMark.startTime,
    end: endMark.startTime,
  });

  pushMeasureToDataLayer(`${VIEWABLE_MARK_NAME} to ${endEventName}`, adType, position);
};

const getAdTypeAndPosition = (slot) => {
  const targeting = slot?.getConfig?.('targeting')?.targeting;

  return { adType: targeting?.ad_type?.[0], position: targeting?.position?.[0] };
};

export const trackSlotRequested = (event) => {
  const { adType, position } = getAdTypeAndPosition(event?.slot);
  if (!position) return;

  const elementId = event?.slot?.getSlotElementId?.();

  performance.mark('slotRequested', { detail: { 'position': position, 'divId': elementId } });

  if (elementId) {
    if (hasGoogleAdInViewportAtLoadMark(elementId)) {
      measureEventToEvent(position, elementId, 'pageLoadStart', 'slotRequested', adType);
    }
    measureViewableToEvent(elementId, position, 'slotRequested', adType);
  }
};

export const trackSlotOnLoad = (event) => {
  const { adType, position } = getAdTypeAndPosition(event?.slot);
  if (!position) return;

  const elementId = event?.slot?.getSlotElementId?.();

  performance.mark('slotOnLoad', { detail: { 'position': position, 'divId': elementId } });
  measureEventToEvent(position, elementId, 'slotRequested', 'slotOnLoad', adType);

  if (elementId) {
    if (hasGoogleAdInViewportAtLoadMark(elementId)) {
      measureEventToEvent(position, elementId, 'pageLoadStart', 'slotOnLoad', adType);
      // This is added here as at this point we now have the ad position
      measureEventToEvent(position, elementId, 'pageLoadStart', VIEWABLE_MARK_NAME, adType);
    }
    measureViewableToEvent(elementId, position, 'slotOnLoad', adType);
  }
};

const viewableMarkedIds = new Set();

let viewabilityObserver;
let googleAdViewabilityDomBootstrapInitialized = false;

const viewabilityObserverCallback = (entries) => {
  entries.forEach((entry) => {
    if (!entry.isIntersecting) return;
    const target = entry.target;
    if (!target?.id || viewableMarkedIds.has(target.id)) return;

    viewableMarkedIds.add(target.id);
    // check if the slot requested mark exists and if so then add a measure (lazy loading might cause the ad to load before it gets into the viewport)
    const slotRequestedMark = performance.getEntriesByType('mark').find(m => m.name === 'slotRequested' && m.detail.divId === target.id);
    // check if the slot on load mark exists and if so then add a measure (lazy loading might cause the ad to load before it gets into the viewport)
    const slotOnLoadMark = performance.getEntriesByType('mark').find(m => m.name === 'slotOnLoad' && m.detail.divId === target.id);

    performance.mark(VIEWABLE_MARK_NAME, { detail: { divId: target.id }});

    if (slotRequestedMark) {
      measureViewableToEvent(target.id, slotRequestedMark.detail.position, 'slotRequested', slotRequestedMark.detail.adType);
    }

    if (slotOnLoadMark) {
      measureViewableToEvent(target.id, slotOnLoadMark.detail.position, 'slotOnLoad', slotOnLoadMark.detail.adType);
    }

    viewabilityObserver.unobserve(target);
  });
};

export const observeAdElement = (element) => {
  if (viewabilityObserver && element?.id) {
    viewabilityObserver.observe(element);
  }
};

const observeAllGoogleAdElements = () => {
  document.querySelectorAll('.google-ad').forEach((el) => {
    if (el.id) {
      markGoogleAdInViewportAtLoadIfNeeded(el);
      observeAdElement(el);
    }
  });
};

export const setupViewabilityObserver = () => {
  viewabilityObserver = new IntersectionObserver(viewabilityObserverCallback);

  observeAllGoogleAdElements();

  if (!googleAdViewabilityDomBootstrapInitialized) {
    googleAdViewabilityDomBootstrapInitialized = true;

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', observeAllGoogleAdElements, { once: true });
    }

    requestAnimationFrame(() => observeAllGoogleAdElements());
  }
};
